import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { message } from "antd";
import type { TablePaginationConfig } from "antd";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { useNavigate } from "react-router-dom";

import AppContext from "../../context/AppContext";
import { SIDER_TABS } from "../../utils/Constants";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getEffectiveAdminTimezone } from "../../utils/dateService";
import { buildReportingSummaryProfitabilityPath } from "../../utils/NavigationUtils";
import { useSportsbookAnalyticsSummary } from "../../queries/sportsbook/useSportsbookAnalyticsSummary";
import { useSportsbookAnalyticsKpis } from "../../queries/sportsbook/useSportsbookAnalyticsKpis";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
import { usePageTitle } from "../CommonComponents/Layout/PageTitleContext";
import {
  filtersMetaToOptions,
  reportingSummaryFiltersToSearchParams,
  type ReportingSummaryFilterKey,
  type ReportingSummaryFiltersValue,
} from "../../queries/sportsbook/reportingSummaryFilterOptions";
import { downloadReportingSummaryTable } from "./reportingSummaryDownload";
import ReportingSummaryHeader from "./ReportingSummaryHeader";
import ReportingSummaryKpis from "./ReportingSummaryKpis";
import ReportingSummaryFilters from "./ReportingSummaryFilters";
import ReportingSummaryTableSection from "./ReportingSummaryTableSection";
import { getReportingSummaryColumns } from "./reportingSummaryColumns";
import { DEFAULT_REPORTING_SUMMARY_PAGE_SIZE } from "./reportingSummaryConstants";
import { coerceReportingSummaryPageSize } from "./reportingSummaryUtils";
import {
  createAllReportingSummaryFilters,
  isReportingSummaryFiltersDirty,
} from "./reportingSummaryFiltersUtils";
import {
  type ReportingSummarySortKey,
  sortReportingSummaryRows,
} from "./reportingSummarySort";
import type { ReportingSummaryRow } from "./reportingSummaryTypes";

import "./stylesReportingSummary.css";

dayjs.extend(utc);
dayjs.extend(timezone);

function ReportingSummary() {
  usePageTitle(AppStrings.SPORTSBOOK_REPORTING_SUMMARY);
  const navigate = useNavigate();
  const { setActiveSidebarTab } = useContext(AppContext) ?? {};
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_REPORTING_SUMMARY_PAGE_SIZE);
  const adminTimezone = getEffectiveAdminTimezone();
  const [reportingDates, setReportingDates] = useState<[Dayjs, Dayjs]>(() => [
    dayjs().tz(adminTimezone).startOf("month"),
    dayjs().tz(adminTimezone).endOf("month"),
  ]);
  const [appliedQueryRange, setAppliedQueryRange] = useState<{
    start_date: string;
    end_date: string;
  } | null>(null);
  const [sortState, setSortState] = useState<{
    field: ReportingSummarySortKey | null;
    order: "ascend" | "descend" | null;
  }>({ field: null, order: null });

  // Filter panel collapsed by default; draft tracks in-progress edits, applied is what's submitted.
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] =
    useState<ReportingSummaryFiltersValue>(createAllReportingSummaryFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<ReportingSummaryFiltersValue>(createAllReportingSummaryFilters);
  const previousRangeRef = useRef<{
    start_date: string;
    end_date: string;
  } | null>(null);

  const derivedRange = useMemo(
    () => ({
      start_date: reportingDates[0].tz(adminTimezone).format("YYYY-MM-DD"),
      end_date: reportingDates[1].tz(adminTimezone).format("YYYY-MM-DD"),
    }),
    [reportingDates, adminTimezone],
  );

  const effectiveRange = appliedQueryRange ?? derivedRange;
  const requestFilters = appliedFilters;

  const {
    rows: tableRows,
    totalRowCount,
    filters: summaryFiltersMeta,
    isLoading: isSummaryLoading,
    isFetching,
    isPlaceholderData,
    isError,
    isSuccess,
    wasRangeClamped,
  } = useSportsbookAnalyticsSummary({
    ...effectiveRange,
    page: currentPage,
    size: pageSize,
    money_type: requestFilters.money_type,
    bet_status: requestFilters.bet_status,
    bet_type: requestFilters.bet_type,
    sport: requestFilters.sport,
    league: requestFilters.league,
    odds_provider: requestFilters.odds_provider,
    state: requestFilters.state,
  });

  const filterOptions = useMemo(
    () => filtersMetaToOptions(summaryFiltersMeta),
    [summaryFiltersMeta],
  );

  const isFilterOptionsLoading = isFetching && filterOptions === null;
  const showTableLoader =
    isSummaryLoading || (isFetching && Boolean(isPlaceholderData));

  // A new reporting window must not inherit restrictions from the previous one.
  useEffect(() => {
    const previous = previousRangeRef.current;
    if (
      previous &&
      (previous.start_date !== effectiveRange.start_date ||
        previous.end_date !== effectiveRange.end_date)
    ) {
      setDraftFilters(createAllReportingSummaryFilters());
      setAppliedFilters(createAllReportingSummaryFilters());
    }
    previousRangeRef.current = effectiveRange;
  }, [effectiveRange]);

  const {
    turnover: kpiTurnover,
    pnl: kpiPnl,
    margin: kpiMargin,
    isFetching: isKpisFetching,
    isError: isKpisError,
  } = useSportsbookAnalyticsKpis({
    ...effectiveRange,
    money_type: requestFilters.money_type,
    bet_status: requestFilters.bet_status,
    bet_type: requestFilters.bet_type,
    sport: requestFilters.sport,
    league: requestFilters.league,
    odds_provider: requestFilters.odds_provider,
    state: requestFilters.state,
  });

  useEffect(() => {
    // Match the SPORTSBOOK_OPTIONS leaf labelKey so the entry stays highlighted
    // while the user is on Reporting Summary.
    setActiveSidebarTab?.(SIDER_TABS.SPORTSBOOK, "SPORTSBOOK_REPORTING_SUMMARY");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    if (isError) {
      message.error(AppStrings.REPORTING_SUMMARY_FETCH_ERROR);
    }
  }, [isError]);

  useEffect(() => {
    if (!isFetching && isSuccess && wasRangeClamped) {
      message.warning(AppStrings.REPORTING_SUMMARY_RANGE_CLAMPED);
    }
  }, [
    isFetching,
    isSuccess,
    wasRangeClamped,
    effectiveRange.start_date,
    effectiveRange.end_date,
  ]);

  const handleReportingRangeQuery = (queryString?: string | null) => {
    setCurrentPage(1);
    if (!queryString) return;
    const params = new URLSearchParams(queryString);
    const start_date = params.get("start_date");
    const end_date = params.get("end_date");
    if (start_date && end_date) {
      setAppliedQueryRange({ start_date, end_date });
    }
  };

  const sortedRows = useMemo(
    () =>
      sortReportingSummaryRows(tableRows, sortState.field, sortState.order),
    [tableRows, sortState.field, sortState.order],
  );

  const handleExport = useCallback(() => {
    downloadReportingSummaryTable(sortedRows);
  }, [sortedRows]);

  const currencySymbol = useCurrencySymbol();

  const columns = useMemo(
    () =>
      getReportingSummaryColumns({
        onOpenProfitability: (record) => {
          const iso = record.date.format("YYYY-MM-DD");
          const params =
            reportingSummaryFiltersToSearchParams(appliedFilters);
          const search = params.toString();
          navigate(
            `${buildReportingSummaryProfitabilityPath(iso)}${
              search ? `?${search}` : ""
            }`,
          );
        },
        currencySymbol,
      }),
    [navigate, currencySymbol, appliedFilters],
  );

  const handleTableChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter:
        | SorterResult<ReportingSummaryRow>
        | SorterResult<ReportingSummaryRow>[],
    ) => {
      const one = Array.isArray(sorter) ? sorter[0] : sorter;
      const field = one?.field as ReportingSummarySortKey | undefined;
      const order = one?.order ?? null;
      setSortState({ field: field ?? null, order });
      setCurrentPage(1);
    },
    [],
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (value: unknown) => {
    setPageSize(coerceReportingSummaryPageSize(value));
    setCurrentPage(1);
  };

  const handleToggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  const handleDraftFilterChange = useCallback(
    (key: ReportingSummaryFilterKey, next: string[]) => {
      setDraftFilters((prev) => ({ ...prev, [key]: next }));
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
    setCurrentPage(1);
  }, [draftFilters]);

  const handleClearFilters = useCallback(() => {
    setDraftFilters(createAllReportingSummaryFilters());
    setAppliedFilters(createAllReportingSummaryFilters());
    setCurrentPage(1);
  }, []);

  const filtersDirty = isReportingSummaryFiltersDirty(
    draftFilters,
    appliedFilters,
  );

  return (
    <>
      <div className="reporting-summary-page">
        <ReportingSummaryHeader
          reportingDates={reportingDates}
          setReportingDates={setReportingDates}
          onReportingRangeQuery={handleReportingRangeQuery}
          startRange={AppStrings.THIS_MONTH}
          onTableDownload={handleExport}
          showFilters={showFilters}
          onToggleFilters={handleToggleFilters}
        />
        {showFilters ? (
          <ReportingSummaryFilters
            options={filterOptions ?? null}
            draft={draftFilters}
            onDraftChange={handleDraftFilterChange}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            isDirty={filtersDirty}
            loading={isFilterOptionsLoading}
          />
        ) : null}
        <ReportingSummaryKpis
          turnover={kpiTurnover}
          pnl={kpiPnl}
          margin={kpiMargin}
          currencySymbol={currencySymbol}
          isFetching={isKpisFetching}
          isError={isKpisError}
        />
        <ReportingSummaryTableSection
          columns={columns}
          dataSource={sortedRows}
          totalRowCount={totalRowCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onTableChange={handleTableChange}
          loading={showTableLoader}
        />
      </div>
    </>
  );
}

export default ReportingSummary;
